import { Vector3 } from "three";
import { Arcs, ArcsOptions } from "../arcs/Arcs";

/**
 * Configuration for lattice cube arc spawning
 */
export interface LatticeArcsConfig {
  /** Size parameter matching morph-particles size (default: 800) */
  size: number;
  /** Maximum arcs at once (default: 6) */
  maxArcs: number;
  /** How often to spawn arcs [min, max] seconds (default: [0.3, 0.8]) */
  spawnInterval: [number, number];
  /** Arc lifetime [min, max] seconds (default: [0.4, 0.8]) */
  arcLifetime: [number, number];
}

const DEFAULT_LATTICE_CONFIG: LatticeArcsConfig = {
  size: 800,
  maxArcs: 6,
  spawnInterval: [0.3, 0.8],
  arcLifetime: [1.2, 2.4],
};

/**
 * Represents an edge in the lattice cube
 */
interface LatticeEdge {
  start: Vector3;
  end: Vector3;
}

/**
 * Lightning arcs for the latticed cube (Network) particle shape.
 * Arcs travel precisely along the lattice edges/bones of the cube.
 */
export class LatticeArcs extends Arcs {
  private latticeConfig: LatticeArcsConfig;
  private active = false;
  private transitionProgress = 0;

  // Pre-computed lattice edges
  private edges: LatticeEdge[] = [];
  private halfSize: number;
  private spacing: number;

  constructor(
    latticeConfig: Partial<LatticeArcsConfig> = {},
    arcsOptions: ArcsOptions = {},
  ) {
    const config = { ...DEFAULT_LATTICE_CONFIG, ...latticeConfig };

    // Configure the base Arcs class for lattice lightning style
    // Use minimal jaggedness since we want arcs to follow edges precisely
    super({
      ...arcsOptions,
      config: {
        animation: {
          horizontalEasing: 0.02, // Very low - keep arcs on the edge
          verticalEasing: 0.02,
          targetChangeChance: 0.02, // Very low randomness
          horizontalRange: 0.05, // Minimal deviation from edge
          verticalRange: 0.05,
          jaggedness: 8, // Low jaggedness for cleaner lines
          segmentsMin: 15,
          segmentsRange: 5,
          curveSegments: 25,
        },
        spawn: {
          maxArcs: config.maxArcs,
          spawnInterval: config.spawnInterval,
          lifetime: config.arcLifetime,
          settlementDelay: 0,
          sineSpeed: 0,
        },
        style: {
          // Electric blue lightning
          glowLayers: [
            [0.12, 0.12, false],
            [0.08, 0.22, false],
            [0.04, 0.38, false],
            [0.02, 0.58, false],
            [0.008, 1.0, true],
          ],
          glowLayersMobile: [
            [0.06, 0.28, false],
            [0.03, 0.52, false],
            [0.01, 1.0, true],
          ],
          glowColor: { r: 0.5, g: 0.8, b: 1.0 }, // Electric blue
          coreColor: { r: 1.0, g: 1.0, b: 1.0 }, // White core
        },
        ...arcsOptions.config,
      },
    });

    this.latticeConfig = config;

    // Calculate lattice dimensions (matching NetworkParticles.ts)
    // Network uses size * 1.6 in morph-particles
    const actualSize = config.size * 1.6;
    this.spacing = actualSize / 8;
    this.halfSize = actualSize / 2;

    // Pre-compute all edges of the lattice
    this.computeLatticeEdges();
  }

  /**
   * Compute all edges of the lattice cube.
   * The lattice has 9 grid points along each axis (0-8).
   * Edges connect adjacent grid points.
   */
  private computeLatticeEdges(): void {
    // Generate all full-length edges (outer and inner wires) along X, Y, and Z
    const min = -this.halfSize;
    const max = this.halfSize;
    const gridSize = 9;
    const step = (max - min) / (gridSize - 1);

    // All edges along X for every (y, z)
    for (let yi = 0; yi < gridSize; yi++) {
      for (let zi = 0; zi < gridSize; zi++) {
        const y = min + yi * step;
        const z = min + zi * step;
        this.edges.push({
          start: new Vector3(min, y, z),
          end: new Vector3(max, y, z),
        });
      }
    }

    // All edges along Y for every (x, z)
    for (let xi = 0; xi < gridSize; xi++) {
      for (let zi = 0; zi < gridSize; zi++) {
        const x = min + xi * step;
        const z = min + zi * step;
        this.edges.push({
          start: new Vector3(x, min, z),
          end: new Vector3(x, max, z),
        });
      }
    }

    // All edges along Z for every (x, y)
    for (let xi = 0; xi < gridSize; xi++) {
      for (let yi = 0; yi < gridSize; yi++) {
        const x = min + xi * step;
        const y = min + yi * step;
        this.edges.push({
          start: new Vector3(x, y, min),
          end: new Vector3(x, y, max),
        });
      }
    }
  }

  /**
   * Set the transition progress (0-1) for fading in/out with shape transitions
   */
  public setTransitionProgress(progress: number): void {
    this.transitionProgress = Math.max(0, Math.min(1, progress));
    this.setIntensity(this.transitionProgress);
    this.active = progress > 0.1;
  }

  /**
   * Check if arcs are currently active
   */
  public isActive(): boolean {
    return this.active;
  }

  /**
   * Update the lattice arcs
   */
  public updateLattice(time: number, deltaTime: number = 1 / 60): void {
    if (!this.active) {
      this.clearArcs();
      return;
    }

    const { spawnInterval, arcLifetime, maxArcs } = this.latticeConfig;
    const [minInterval, maxInterval] = spawnInterval;

    // Try to spawn multiple arcs - each slot has independent spawn chance
    const currentArcCount = this.getArcCount();
    const availableSlots = maxArcs - currentArcCount;

    for (let slot = 0; slot < availableSlots; slot++) {
      const baseChance = deltaTime / ((minInterval + maxInterval) / 2);
      const spawnChance = baseChance * (0.5 + Math.random() * 0.5);

      if (Math.random() < spawnChance) {
        this.spawnLatticeArc(arcLifetime);
      }
    }

    // Update existing arcs
    this.update(deltaTime);
  }

  /**
   * Spawn a single arc along a random lattice edge
   */
  private spawnLatticeArc(arcLifetime: [number, number]): void {
    if (this.edges.length === 0) return;

    // Pick a random edge
    const edge = this.edges[Math.floor(Math.random() * this.edges.length)];
    if (!edge) return;

    // Randomly choose direction (start to end or end to start)
    const reverse = Math.random() < 0.5;
    const startPoint = reverse ? edge.end.clone() : edge.start.clone();
    const endPoint = reverse ? edge.start.clone() : edge.end.clone();

    const [minLife, maxLife] = arcLifetime;
    this.spawnArc({
      startPoint,
      endPoint,
      lifetime: minLife + Math.random() * (maxLife - minLife),
      intensity: 0.7 + Math.random() * 0.3,
    });
  }

  public dispose(): void {
    super.dispose();
    this.edges = [];
  }
}
